// Step 2 — Style & Colour.
//
// Ported 2026-05-27 from the canonical stage wizard at
// /var/www/kitchenos/tool-app/index.html (kos-tkc-card, lines 2376–2680).
// Step 2 collects the door range + colour selection, followed by the
// Worktops / Storage / Appliances option cards below the range grid.
//
// Layout per range card:
//   hero (price-pill overlay + zoom hint, lightbox on click)
//   swatch-bar  (Colours: label + circular swatches + current-colour name)
//   body
//     left:  name + sub-chips + description + meta (colour count + PTO) + View Specs ▾
//     right: door close-up thumbnail + "Door close-up" label
//   "Select This Style" full-width CTA → "✓ Selected"

import type {
  AppliancesSelection,
  StorageChoice,
  StyleChoices,
  WorktopChoice,
} from "../types";
import type { StepContext } from "./index";
import { escape, escapeAttr, authBarButtonHtml, wireAuthBarButton, openChatPopup } from "./index";
import { svgIcon } from "./icons";
import { mountRangeBrowser } from "./range-browser";
import type { NormalisedRange } from "./range-browser";

/** One icon per appliance GROUP — the "type" the brief refers to. The options
 *  inside a group (60cm vs 75cm, gas vs induction) are variants of the same
 *  object, so they stay as text. */
const APPLIANCE_ICONS: Record<string, string> = {
  sink: svgIcon(`<path d="M3 11h18v5a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3z"/><path d="M12 11V7a3 3 0 0 1 3-3h1"/><circle cx="12" cy="14.5" r="1.2"/>`),
  fridge: svgIcon(`<rect x="6" y="2" width="12" height="20" rx="2"/><path d="M6 10h12"/><path d="M9 6v2M9 13v2"/>`),
  hob: svgIcon(`<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="2"/><circle cx="15.5" cy="8.5" r="2"/><circle cx="8.5" cy="15.5" r="2"/><circle cx="15.5" cy="15.5" r="2"/>`),
  oven: svgIcon(`<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 8h18"/><circle cx="7" cy="5.5" r=".8"/><path d="M7 12h10"/>`),
  extractor: svgIcon(`<path d="M3 9 6 4h12l3 5z"/><path d="M3 9h18v3H3z"/><path d="M8 15v3M12 15v4M16 15v3"/>`),
  microwave: svgIcon(`<rect x="2" y="5" width="20" height="14" rx="2"/><rect x="5" y="8" width="10" height="8" rx="1"/><path d="M18 9v6"/>`),
  dishwasher: svgIcon(`<rect x="4" y="2" width="16" height="20" rx="2"/><path d="M4 7h16"/><circle cx="12" cy="14.5" r="3.5"/>`),
  washing: svgIcon(`<rect x="4" y="2" width="16" height="20" rx="2"/><path d="M4 7h16"/><circle cx="12" cy="14.5" r="4"/><circle cx="12" cy="14.5" r="1.5"/>`),
  lighting: svgIcon(`<path d="M9 18h6"/><path d="M10 21h4"/><path d="M12 3a6 6 0 0 0-3.5 10.9V15h7v-1.1A6 6 0 0 0 12 3z"/>`),
};

const STORAGE_OPTIONS: StorageChoice[] = [
  "Bin", "Corner Solutions", "Larder", "Internal Drawers",
];
// Trade worktop categories — written to styleChoices.worktop (the WorktopChoice
// contract the retailer's customer-brief record reads + edits, so the wizard
// selection syncs there). Value === label; these are categories, not visual
// finishes. (The finer visual material — white_quartz etc. — lives separately on
// worktop_material, set by the Aria/AI capture and consumed by the render engine.)
const WORKTOP_CHOICES: Array<[string, string]> = [
  ["Laminate", "Laminate"], ["Solid Wood", "Solid Wood"], ["Quartz", "Quartz"],
  ["Granite", "Granite"], ["Composite", "Composite"], ["Dekton", "Dekton"], ["Porcelain", "Porcelain"],
];
// Handles and flooring were briefly cut from the wizard in the 2026-08-10
// redesign, then reinstated on 2026-08-20: the objection was that they
// front-loaded the step, not that the answers are worthless. They now sit
// inside the collapsed "Tell us more" panel alongside worktop and storage, so
// the step stays short by default and the AI-render prompt engine and Aria
// (both of which read StyleChoices.handleStyle / .flooringType) keep getting
// real values from customers who choose to fill them in.
// Handles are asked as SHAPE then FINISH (Steve, 2026-08-20). The old list
// mixed the two — four of its eight entries were the same bar handle in a
// different metal, which crowded the grid and still could not express "cup
// handle in brass" even though that is an everyday specification.
const HANDLE_STYLE_OPTIONS: Array<[string, string]> = [
  ["bar", "Bar"], ["cup", "Cup Handle"], ["knob", "Knob"],
  ["handleless", "Handleless (J-pull)"], ["unknown", "Not sure yet"],
];
/** Shapes that come in a choice of metal. Bar, cup and knob all do in real
 *  ranges; handleless has no hardware to finish, and "not sure yet" is not a
 *  shape. Membership here is what makes the Finish question appear. */
const HANDLE_FINISHABLE = new Set(["bar", "cup", "knob"]);
const HANDLE_FINISH_OPTIONS: Array<[string, string]> = [
  ["brass", "Brass"], ["steel", "Steel"], ["black", "Black"], ["chrome", "Chrome"],
];
const FLOORING_OPTIONS: Array<[string, string]> = [
  ["light_oak", "Light Oak"], ["dark_oak", "Dark Oak"], ["herringbone", "Herringbone Wood"],
  ["stone_tile", "Stone Tile"], ["porcelain", "Porcelain Tile"], ["concrete", "Concrete / Polished"],
  ["lvt", "Vinyl / LVT"], ["unknown", "Not sure yet"],
];
const APPLIANCE_GROUPS: Array<{
  key: keyof AppliancesSelection;
  label: string;
  options: string[];
}> = [
  { key: "sink",       label: "Sink & Taps",   options: ["Single", "1.5 Bowl", "Double", "Boiling tap", "Filtered water tap"] },
  { key: "fridge",     label: "Refrigeration", options: ["Larder", "70-30", "60-40", "50-50", "American", "Wine cooler"] },
  { key: "hob",        label: "Hob",           options: ["Gas", "Electric", "Induction", "Downdraught", "60cm", "75cm", "90cm"] },
  { key: "oven",       label: "Oven",          options: ["Single", "Double", "Built Under", "Built In", "Warming drawer", "Steam oven"] },
  { key: "extractor",  label: "Extractor",     options: ["Chimney", "Canopy", "Downdraught"] },
  { key: "microwave",  label: "Microwave",     options: ["Standard", "Combi"] },
  { key: "dishwasher", label: "Dishwasher",    options: ["Standard 60cm", "Slimline 45cm", "Fully integrated", "Semi integrated"] },
  { key: "washing",    label: "Washing",       options: ["Washing machine", "Tumble dryer", "Washer dryer"] },
];
const LIGHTING_OPTIONS = ["Under Cabinet", "Plinth", "In-Cabinet"];

// Expand-state for the optional "Tell us more" detail section.
//
// Deliberately MODULE-scope: render() rewrites host.innerHTML wholesale, and
// step 3's Back button calls ctx.goTo(1), which re-runs mountStep2 from
// scratch. Module scope survives both, so the section never re-collapses under
// the customer mid-step.
//
// NOT localStorage: the wizard shell is in-memory by design ("State is
// in-memory only — no localStorage. Page reload restarts the flow." —
// wizard/index.ts:10). A reload drops the customer at step 1 with empty state,
// so carrying one accordion's flag across that would be incoherent. Collapsed
// on every fresh load IS the intended default.
let detailsOpen = false;

export function mountStep2(host: HTMLElement, ctx: StepContext): void {
  // One render — the range browser owns its own loading/error/filter state and
  // repaints only its own subtree, so the step no longer has to re-render
  // wholesale when the catalogue arrives.
  render(host, ctx);
}

/** Bridge the extracted browser to wizard state. This is the ONLY place the
 *  catalogue UI touches WizardState — everything else in range-browser.ts is
 *  host-agnostic, which is what lets the same component back a browse-only
 *  "Our Kitchens" page. */
function browserHostFor(ctx: StepContext): Parameters<typeof mountRangeBrowser>[1] {
  return {
    // Scopes the catalogue to the suppliers this retailer has enabled. Falls
    // back to every published range when they have not curated a list.
    retailerId: ctx.state.retailerId,
    getSelection: () => ({
      rangeId: ctx.state.style.doorRangeId,
      colourName: ctx.state.style.colourName,
    }),
    selectRange: (range: NormalisedRange, colourName: string) => {
      const next: StyleChoices = {
        ...ctx.state.style,
        doorRangeId: range.id,
        doorRange: range.name,
        colourName,
      };
      ctx.setState({ ...ctx.state, style: next });
    },
    changeColour: (_range: NormalisedRange, colourName: string) => {
      ctx.setState({ ...ctx.state, style: { ...ctx.state.style, colourName } });
    },
    afterSelect: scrollToWorktops,
  };
}

// ── Render ──────────────────────────────────────────────────────────────────

function render(host: HTMLElement, ctx: StepContext): void {
  host.innerHTML = `
    <div class="wiz-card">
      <h2 class="wiz-card__title">${escape(ctx.state.branding.welcomeHeading)}</h2>
      <p class="wiz-card__subtitle">${escape(ctx.state.branding.welcomeSubtext)}</p>
      <!-- The step gates nothing (Next just advances). Say so, or customers
           who don't know what they want stall here rather than moving on. -->
      <p class="wiz-card__subtitle wiz-optional-note">
        Every question here is optional — pick anything that catches your eye, or
        skip straight on and talk it through with your designer.
      </p>

      <!-- Search / filters / range grid — mounted by range-browser.ts -->
      <div data-role="browser-host"></div>
    </div>

    <div class="wiz-more" data-role="more">
      <button
        type="button"
        class="wiz-more__toggle ${detailsOpen ? "wiz-more__toggle--open" : ""}"
        data-action="more-toggle"
        aria-expanded="${detailsOpen ? "true" : "false"}"
        aria-controls="wiz-more-panel"
      >
        <span class="wiz-more__text">
          <span class="wiz-more__label">Tell us more</span>
          <span class="wiz-more__hint">Appliances, worktops, handles, storage and flooring &mdash; all optional</span>
        </span>
        <svg class="wiz-more__chev" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9.5 12 15.5 18 9.5"/></svg>
      </button>

      <div
        class="wiz-more__panel ${detailsOpen ? "wiz-more__panel--open" : ""}"
        id="wiz-more-panel"
        data-role="more-panel"
      >
        <!-- Order (Steve, 2026-08-20): appliances first (what people actually
             have opinions about), then worktop, handles, storage, flooring.
             Everything in here is optional and behind the toggle — the point
             of the redesign was to stop these questions front-loading the
             step, not to stop asking them. -->
        <div class="wiz-card">
          <h3 class="wiz-card__title">Appliances</h3>
          <p class="wiz-card__subtitle">Tell us what's going in your dream kitchen.</p>
          <div data-role="appliance-host"></div>
        </div>

        <div class="wiz-card">
          <h3 class="wiz-card__title">Worktop</h3>
          <p class="wiz-card__subtitle">What worktop are you thinking about?</p>
          ${materialGrid("worktop-group", WORKTOP_CHOICES, ctx.state.style.worktop)}
        </div>

        <div class="wiz-card">
          <h3 class="wiz-card__title">Handles</h3>
          <p class="wiz-card__subtitle">What handles are you drawn to?</p>
          ${materialGrid("handle-group", HANDLE_STYLE_OPTIONS, ctx.state.style.handleStyle)}
          <!-- Finish only exists for shapes that come in a choice of metal, so
               the sub-question is rendered but hidden until one is picked
               (rather than injected on the fly) — the markup stays static and
               wireMaterialGrid can bind to it once, exactly like every other
               grid on this step. -->
          <div
            class="wiz-subfield"
            data-role="handle-finish-wrap"
            ${HANDLE_FINISHABLE.has(ctx.state.style.handleStyle || "") ? "" : "hidden"}
          >
            <p class="wiz-card__subtitle">And in which finish?</p>
            ${materialGrid("handle-finish-group", HANDLE_FINISH_OPTIONS, ctx.state.style.handleFinish)}
          </div>
        </div>

        <div class="wiz-card">
          <h3 class="wiz-card__title">Storage solutions</h3>
          <p class="wiz-card__subtitle">Choose any that interest you.</p>
          <div class="wiz-toggle-group" data-role="storage-group">
            ${STORAGE_OPTIONS.map((s) =>
              toggleButton(s, ctx.state.style.storage.includes(s)),
            ).join("")}
          </div>
        </div>

        <div class="wiz-card">
          <h3 class="wiz-card__title">Flooring</h3>
          <p class="wiz-card__subtitle">What flooring do you have or are you planning?</p>
          ${materialGrid("flooring-group", FLOORING_OPTIONS, ctx.state.style.flooringType)}
        </div>
      </div>
    </div>

    <div class="wiz-bottombar wiz-bottombar--step2">
      <button type="button" class="wiz-bb-btn" data-action="back" title="Back to room plan">
        <span>← Back</span>
      </button>
      <button type="button" class="wiz-bb-btn" data-action="save" title="Save your progress">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
        <span>Save</span>
      </button>
      <button type="button" class="wiz-bb-btn" data-action="chat" title="Chat with us">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        <span>Chat</span>
      </button>
      ${authBarButtonHtml(ctx.state)}
      <button type="button" class="wiz-bb-next" data-action="next">Next: Your Details →</button>
    </div>
  `;

  // Wire primary navigation FIRST — before the catalogue mounts below. A bad
  // catalogue row used to be able to throw inside the load promise chain, which
  // swallowed it and left the step with dead Back/Next handlers. Wiring nav
  // up-front guarantees the step is always navigable. (Bug 10/11)
  //
  // The browser now owns its own async load and repaints only its own subtree,
  // so this render runs exactly once — but the ordering still matters, because
  // mountRangeBrowser's first synchronous paint can still throw on bad data.
  host.querySelector<HTMLButtonElement>('[data-action=back]')!
    .addEventListener("click", () => ctx.goTo(0));
  host.querySelector<HTMLButtonElement>('[data-action=next]')!
    .addEventListener("click", () => ctx.goTo(2));
  wireAuthBarButton(host, ctx);

  // Wire the "Tell us more" accordion alongside nav, BEFORE the catalogue
  // renders below — same reasoning as Bug 10/11: a malformed catalogue row
  // must not be able to leave the toggle dead and the optional section
  // permanently unreachable.
  wireMoreToggle(host);

  // These depend on live catalogue data and can throw on a malformed row.
  // Contain + LOG any failure so it never silently breaks the rest of the step,
  // and so the real error is visible for diagnosis. (Bug 10/11/13)
  try {
    mountRangeBrowser(host.querySelector<HTMLElement>("[data-role=browser-host]")!, browserHostFor(ctx));
    renderAppliances(host.querySelector<HTMLElement>("[data-role=appliance-host]")!, ctx);
    wireMaterialGrid(host, ctx, "worktop-group", "worktop");
    wireHandles(host, ctx);
    wireMaterialGrid(host, ctx, "flooring-group", "flooringType");
    wireStorage(host, ctx);
  } catch (err) {
    console.error("[wizard step-2] catalogue render failed (navigation still works):", err);
  }

  // Footer / stage-style auxiliary actions (Back/Next/Login wired above).
  const saveBtn = host.querySelector<HTMLButtonElement>('[data-action=save]');
  const chatBtn = host.querySelector<HTMLButtonElement>('[data-action=chat]');

  saveBtn?.addEventListener("click", () => {
    try {
      const payload = {
        retailerId: ctx.state.retailerId,
        room: ctx.state.room,
        style: ctx.state.style,
        stepIndex: ctx.state.stepIndex,
        savedAt: new Date().toISOString(),
      };
      window.localStorage.setItem("tkos-wiz-save-v1", JSON.stringify(payload));
      toast(saveBtn, "Saved ✓");
    } catch {
      toast(saveBtn, "Couldn't save");
    }
  });

  chatBtn?.addEventListener("click", () => openChatPopup(ctx));
}

/** Temporary inline confirmation on a button — shows `msg` for ~1.4s. */
function toast(btn: HTMLElement, msg: string): void {
  const orig = btn.getAttribute("data-toast-orig") ?? btn.innerHTML;
  btn.setAttribute("data-toast-orig", orig);
  btn.innerHTML = `<span>${msg}</span>`;
  window.setTimeout(() => { btn.innerHTML = orig; }, 1400);
}

/** Smooth-scroll to the worktops section (next card after the range grid).
 *  Falls back to a window scroll if the target isn't present for any reason. */
function scrollToWorktops(): void {
  // Scroll to the "Tell us more" section after a style is chosen.
  //
  // This used to target the worktop group directly. Since the 2026-08-10
  // reorder, worktop lives INSIDE the collapsed accordion — and scrolling to a
  // display:none element does nothing, which is why picking a style appeared to
  // do nothing at all. Open the section first, then scroll to the toggle so the
  // customer sees the heading and the newly-revealed options together.
  const section = document.querySelector<HTMLElement>('[data-role="more"]');
  if (!section) return;

  // Scroll to it, but deliberately do NOT expand it.
  //
  // An earlier pass opened the section here so the revealed options were
  // visible. That made it read as mandatory — and because `detailsOpen` is
  // module-scoped, one style selection left it expanded for the rest of the
  // session, including on every return to this step. The section is optional,
  // so bringing the toggle into view is the whole job; opening it is the
  // customer's call.
  try { section.scrollIntoView({ behavior: "smooth", block: "start" }); }
  catch { section.scrollIntoView(); }
}

// ── Worktops / Storage / Appliances (unchanged from pre-port wizard) ────────

function toggleButton(label: string, on: boolean): string {
  return `
    <label class="wiz-toggle ${on ? "wiz-toggle--on" : ""}" data-toggle-value="${escapeAttr(label)}">
      <input type="checkbox" ${on ? "checked" : ""} />
      <span>${escape(label)}</span>
    </label>
  `;
}

// Single-select material grid (worktop / handles / flooring) → top-level enums.
function optionButton(value: string, label: string, on: boolean): string {
  return `
    <label class="wiz-toggle ${on ? "wiz-toggle--on" : ""}" data-opt-value="${escapeAttr(value)}">
      <input type="checkbox" ${on ? "checked" : ""} />
      <span>${escape(label)}</span>
    </label>
  `;
}
function materialGrid(role: string, opts: Array<[string, string]>, current: string | undefined): string {
  return `<div class="wiz-toggle-group" data-role="${role}">`
    + opts.map(([v, l]) => optionButton(v, l, (current || "") === v)).join("")
    + `</div>`;
}
function wireMaterialGrid(
  host: HTMLElement, ctx: StepContext, role: string,
  key: "worktopMaterial" | "handleStyle" | "handleFinish" | "flooringType" | "worktop",
  /** Optional hook for a grid whose choice affects more than its own field.
   *  Returns extra `style` fields to commit in the SAME setState — important
   *  for the handles pair, where clearing a now-irrelevant finish must not be
   *  a second state write that could race the first. Any DOM work (showing or
   *  hiding a dependent question) can be done as a side effect here. */
  onSelect?: (next: string) => Partial<StyleChoices> | void,
): void {
  const group = host.querySelector<HTMLElement>(`[data-role=${role}]`);
  if (!group) return;
  group.addEventListener("click", (e) => {
    const lbl = (e.target as HTMLElement).closest<HTMLElement>("[data-opt-value]");
    if (!lbl) return;
    e.preventDefault(); // label→checkbox synthetic-click double-fire guard (see wireWorktop)
    const value = lbl.dataset.optValue || "";
    const cur = ctx.state.style[key];
    // "" = skip → default. Cast to WorktopChoice (a subtype of string) so this
    // one setter serves both the string enum fields and the WorktopChoice field.
    const next = ((value === "unknown" || cur === value) ? "" : value) as WorktopChoice;
    const extra = onSelect ? (onSelect(next) || {}) : {};
    ctx.setState({ ...ctx.state, style: { ...ctx.state.style, [key]: next, ...extra } });
    group.querySelectorAll<HTMLElement>("[data-opt-value]").forEach((el) => {
      const on = el.dataset.optValue === next && next !== "";
      el.classList.toggle("wiz-toggle--on", on);
      const cb = el.querySelector<HTMLInputElement>("input");
      if (cb) cb.checked = on;
    });
  });
}

/** Handles = shape + finish, two grids that have to stay consistent.
 *
 *  Picking a shape with no finish (handleless, or clearing the choice) must
 *  also DROP any finish already chosen — otherwise "handleless, brushed brass"
 *  reaches the retailer, and submit.ts would recombine a finish onto a shape
 *  that cannot carry one. The clear happens in the same state write as the
 *  shape change, and the finish grid is repainted so the UI can't show a
 *  selected swatch for a value that is no longer in state. */
function wireHandles(host: HTMLElement, ctx: StepContext): void {
  const wrap = host.querySelector<HTMLElement>("[data-role=handle-finish-wrap]");
  const finishGroup = host.querySelector<HTMLElement>("[data-role=handle-finish-group]");

  wireMaterialGrid(host, ctx, "handle-group", "handleStyle", (next) => {
    const finishable = HANDLE_FINISHABLE.has(next);
    if (wrap) wrap.hidden = !finishable;
    if (finishable) return;
    // Not finishable → clear the swatches as well as the stored value.
    finishGroup?.querySelectorAll<HTMLElement>("[data-opt-value]").forEach((el) => {
      el.classList.remove("wiz-toggle--on");
      const cb = el.querySelector<HTMLInputElement>("input");
      if (cb) cb.checked = false;
    });
    return { handleFinish: "" };
  });

  wireMaterialGrid(host, ctx, "handle-finish-group", "handleFinish");
}

function wireStorage(host: HTMLElement, ctx: StepContext): void {
  const group = host.querySelector<HTMLElement>("[data-role=storage-group]");
  if (!group) return;
  group.addEventListener("click", (e) => {
    const lbl = (e.target as HTMLElement).closest<HTMLElement>("[data-toggle-value]");
    if (!lbl) return;
    // See wireWorktop: prevent the hidden checkbox's synthetic click double-fire.
    e.preventDefault();
    const value = lbl.dataset.toggleValue as StorageChoice;
    const cur = new Set(ctx.state.style.storage);
    if (cur.has(value)) cur.delete(value); else cur.add(value);
    const nextArr = [...cur];
    ctx.setState({ ...ctx.state, style: { ...ctx.state.style, storage: nextArr } });
    lbl.classList.toggle("wiz-toggle--on", cur.has(value));
    const cb = lbl.querySelector<HTMLInputElement>("input");
    if (cb) cb.checked = cur.has(value);
  });
}

/** "Tell us more" accordion — reveals the OPTIONAL detail cards (handles,
 *  flooring, storage, appliances), collapsed by default.
 *
 *  The panel is only ever hidden with CSS `display:none`; it is NEVER removed
 *  from the DOM. That is what keeps wireMaterialGrid / wireStorage /
 *  renderAppliances working untouched — their `host.querySelector([data-role=…])`
 *  lookups still resolve, their delegated listeners stay attached, and a
 *  collapse loses no selection. Mirrors the range card's View-Specs toggle
 *  idiom (`kos-open`). */
function wireMoreToggle(host: HTMLElement): void {
  const btn = host.querySelector<HTMLButtonElement>("[data-action=more-toggle]");
  const panel = host.querySelector<HTMLElement>("[data-role=more-panel]");
  if (!btn || !panel) return;

  const paint = (): void => {
    panel.classList.toggle("wiz-more__panel--open", detailsOpen);
    btn.classList.toggle("wiz-more__toggle--open", detailsOpen);
    btn.setAttribute("aria-expanded", detailsOpen ? "true" : "false");
  };
  // Re-assert the remembered state: render() rebuilds this markup from
  // scratch, so after a re-render the DOM must be reconciled to detailsOpen.
  paint();

  btn.addEventListener("click", () => {
    detailsOpen = !detailsOpen;
    paint();
  });
}

function renderAppliances(host: HTMLElement, ctx: StepContext): void {
  const a = ctx.state.style.appliances;
  // Every appliance group (including Lighting) is multi-select — render them
  // uniformly and drive them with one delegated handler.
  const groups: Array<{ key: keyof AppliancesSelection; label: string; options: readonly string[] }> = [
    ...APPLIANCE_GROUPS,
    { key: "lighting", label: "Lighting", options: LIGHTING_OPTIONS },
  ];
  host.innerHTML = groups.map((g) => `
    <div class="wiz-field">
      <span class="wiz-field__label wiz-field__label--icon">
        ${APPLIANCE_ICONS[g.key] || ""}<span>${escape(g.label)}</span>
      </span>
      <div class="wiz-toggle-group" data-app-key="${escape(g.key)}">
        ${g.options.map((opt) => toggleButton(opt, (a[g.key]).includes(opt))).join("")}
      </div>
    </div>
  `).join("");

  host.addEventListener("click", (e) => {
    const lbl = (e.target as HTMLElement).closest<HTMLElement>("[data-toggle-value]");
    if (!lbl) return;
    // See wireWorktop: prevent the hidden checkbox's synthetic click double-fire.
    e.preventDefault();
    const group = lbl.parentElement;
    const key = group?.dataset.appKey as keyof AppliancesSelection | undefined;
    if (!key) return;
    const value = lbl.dataset.toggleValue!;
    const cur = ctx.state.style.appliances;
    const set = new Set<string>(cur[key]);
    if (set.has(value)) set.delete(value); else set.add(value);
    const next: AppliancesSelection = { ...cur, [key]: [...set] };
    ctx.setState({ ...ctx.state, style: { ...ctx.state.style, appliances: next } });
    lbl.classList.toggle("wiz-toggle--on", set.has(value));
  });
}

